import React, { useState, useEffect, useRef } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Tooltip,
} from "@mui/material";

import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import axios from "axios";
import Swal from "sweetalert2";
import SignatureField from "../../../components/SignatureField";

const headerFieldSx = {
  "& .MuiInputBase-root": {
    height: 40,
    fontSize: "0.900rem",
    padding: "0 10px",
  },
  "& .MuiInputLabel-root": { fontSize: "0.875rem" },
  "& .MuiSvgIcon-root": { display: "none" },
};

const pickNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v === 0) return v;
    if (v !== undefined && v !== null) {
      const s = String(v).trim();
      if (s && s.toLowerCase() !== "null" && s.toLowerCase() !== "undefined")
        return v;
    }
  }
  return "";
};

const toDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleDateString();
};

const pad2 = (n) => String(n).padStart(2, "0");
const fmtYmdDateTime = (val) => {
  if (!val) return "";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = pad2(d.getMonth() + 1);
  const dd = pad2(d.getDate());
  let h = d.getHours();
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  const hh = pad2(h);
  const min = pad2(d.getMinutes());
  return `${yyyy}-${mm}-${dd} ${hh}:${min} ${ampm}`;
};

const fmtMMDDYYYY = (val) => {
  if (!val) return "N/A";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "N/A";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const ensureArray = (v) => {
  if (Array.isArray(v)) return v;
  if (typeof v === "string") {
    const t = v.trim();
    if (!t) return [];
    try {
      const parsed = JSON.parse(t);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const ARMHSITPPreview = ({ data, onClose }) => {
  const [itp, setItp] = useState(null);
  const [form, setForm] = useState({});
  const [formMeta, setFormMeta] = useState(null); // full form record
  const [signatures, setSignatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [patientSigLink, setPatientSigLink] = useState("");
  const [guardianSigLink, setGuardianSigLink] = useState("");
  const [openClientSignatureModal, setOpenClientSignatureModal] =
    useState(false);
  
  // Removed showSuccessAlert state as we are using Swal now

  const clientSigCanvasRef = useRef(null);

  const { patientFormId } = data || {};

  const openClientModal = () => setOpenClientSignatureModal(true);
  const closeClientModal = () => {
    setOpenClientSignatureModal(false);
    if (clientSigCanvasRef.current) {
      clientSigCanvasRef.current.clear();
    }
  };

  const handleSaveClientSignature = () => {
    if (!clientSigCanvasRef.current) return;
    const dataUrl = clientSigCanvasRef.current
      .getCanvas()
      .toDataURL("image/png");
    setPatientSigLink(dataUrl);
    setSignatures((prev) => ({ ...prev, client: dataUrl }));
    closeClientModal();
  };

  // Client signature delete
  const handleDeleteClientSignature = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this signature?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
      didOpen: () => {
        const swalEl = document.querySelector(".swal2-container");
        if (swalEl) swalEl.style.zIndex = "9999";
      },
    }).then(async (result) => {
      if (result.isConfirmed) {
        setPatientSigLink("");
        setSignatures((prev) => ({ ...prev, client: "" }));

        try {
          const token = localStorage.getItem(
            "neuro_patientportal_auth_token"
          );
          const client = axios.create({
            baseURL: API_BASE_URL,
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              Accept: "application/json",
              "Content-Type": "application/json",
            },
          });

          let payload;
          if (formMeta) {
            payload = {
              ...formMeta,
              Id: formMeta.Id ?? formMeta.id ?? patientFormId,
              patientFormId,
              clientsBase64: "",
              clientsURL: "",
              patientSignatureURL: "",
              clientSignatureURL: "",
              imageURLClientSignature: "",
            };
          } else {
            payload = {
              patientFormId,
              clientsBase64: "",
              clientsURL: "",
            };
          }

          await client.post("/Forms/AddPatientForm", payload);

          if (formMeta) {
            setFormMeta((prev) =>
              prev
                ? {
                    ...prev,
                    clientsBase64: "",
                    clientsURL: "",
                    patientSignatureURL: "",
                    clientSignatureURL: "",
                    imageURLClientSignature: "",
                  }
                : prev
            );
          }
          setForm((prev) =>
            prev
              ? {
                  ...prev,
                  clientsBase64: "",
                  clientsURL: "",
                  patientSignatureURL: "",
                  clientSignatureURL: "",
                  imageURLClientSignature: "",
                }
              : prev
          );

          Swal.fire({
            title: "Deleted!",
            text: "Client signature has been removed.",
            icon: "success",
            timer: 1500,
            showConfirmButton: false,
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) swalEl.style.zIndex = "9999";
            },
          });
        } catch (e) {
          console.error(e);
          Swal.fire({
            title: "Error!",
            text: "Failed to remove signature.",
            icon: "error",
            didOpen: () => {
              const swalEl = document.querySelector(".swal2-container");
              if (swalEl) swalEl.style.zIndex = "9999";
            },
          });
        }
      }
    });
  };

  // Guardian signature is read-only in UI

  const handleSaveForm = async () => {
    if (!patientFormId) return;
    try {
      const token = localStorage.getItem(
        "neuro_patientportal_auth_token"
      );
      const client = axios.create({
        baseURL: API_BASE_URL,
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });

      const isClientBase64 =
        patientSigLink &&
        typeof patientSigLink === "string" &&
        patientSigLink.startsWith("data:");
      const isGuardianBase64 =
        guardianSigLink &&
        typeof guardianSigLink === "string" &&
        guardianSigLink.startsWith("data:");

      let payload;
      if (formMeta) {
        payload = {
          ...formMeta,
          Id: formMeta.Id ?? formMeta.id ?? patientFormId,
          patientFormId,
          ...(isClientBase64 && { clientsBase64: patientSigLink }),
          ...(isGuardianBase64 && { guardianBase64: guardianSigLink }),
          // NOTE: do not blank clientsURL/guardianURL here
        };
      } else {
        payload = {
          patientFormId,
          ...(isClientBase64 && { clientsBase64: patientSigLink }),
          ...(isGuardianBase64 && { guardianBase64: guardianSigLink }),
        };
      }

      await client.post("/Forms/AddPatientForm", payload);

      if (formMeta && isClientBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }
      if (formMeta && isGuardianBase64) {
        setFormMeta((prev) =>
          prev ? { ...prev, guardianBase64: guardianSigLink } : prev
        );
      }
      if (isClientBase64) {
        setForm((prev) =>
          prev ? { ...prev, clientsBase64: patientSigLink } : prev
        );
      }
      if (isGuardianBase64) {
        setForm((prev) =>
          prev ? { ...prev, guardianBase64: guardianSigLink } : prev
        );
      }

      // --- Updated SweetAlert Success Popup with zIndex fix ---
      Swal.fire({
        title: "Form Saved",
        text: "Form saved successfully",
        icon: "success",
        confirmButtonColor: "#3085d6",
        confirmButtonText: "OK",
        didOpen: () => {
          // Ensure popup is above modal
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      }).then(() => {
        // Logic to close the preview form after clicking OK
        if (onClose) {
          onClose();
        } else {
          try {
            const backdrop = document.querySelector(".MuiBackdrop-root");
            if (backdrop) {
              backdrop.click();
            } else {
              const closeButton = document.querySelector('[aria-label="close"]');
              if (closeButton) {
                closeButton.click();
              } else {
                window.history.back();
              }
            }
          } catch (err) {
            console.error("Could not close form:", err);
            window.location.reload();
          }
        }
      });

    } catch (e) {
      console.error(e);
      Swal.fire({
        title: "Error!",
        text: "Failed to save form.",
        icon: "error",
        didOpen: () => {
          const swalEl = document.querySelector(".swal2-container");
          if (swalEl) swalEl.style.zIndex = "9999";
        },
      });
    }
  };

  useEffect(() => {
    if (!patientFormId) return;
    let active = true;

    const fetchSignatures = async (noteData, baseForm, token) => {
      const loaded = {};

      // 1) Try Base64 for client & guardian first
      const clientBase64 = pickNonEmpty(
        noteData?.clientsBase64,
        baseForm?.clientsBase64,
        noteData?.clientSignatureBase64,
        baseForm?.clientSignatureBase64,
        noteData?.clientBase64,
        baseForm?.clientBase64
      );
      const guardianBase64 = pickNonEmpty(
        noteData?.guardianBase64,
        baseForm?.guardianBase64,
        noteData?.guardianSignatureBase64,
        baseForm?.guardianSignatureBase64
      );

      if (
        clientBase64 &&
        typeof clientBase64 === "string" &&
        clientBase64.toLowerCase().includes("base64")
      ) {
        loaded.client = clientBase64;
        if (active) setPatientSigLink(clientBase64);
      }
      if (
        guardianBase64 &&
        typeof guardianBase64 === "string" &&
        guardianBase64.toLowerCase().includes("base64")
      ) {
        loaded.guardian = guardianBase64;
        if (active) setGuardianSigLink(guardianBase64);
      }

      // 2) Fallback to URL/path fields if base64 not found
      const urls = {
        client:
          !loaded.client &&
          pickNonEmpty(
            noteData?.clientSignaturePath,
            noteData?.clientSignatureURL,
            noteData?.clientsURL,
            baseForm?.clientSignatureURL,
            baseForm?.patientSignatureURL,
            baseForm?.clientsURL
          ),
        guardian:
          !loaded.guardian &&
          pickNonEmpty(
            noteData?.guardianSignatureURL,
            baseForm?.guardianURL,
            baseForm?.guardianSignatureURL
          ),
      };

      for (const [key, path] of Object.entries(urls)) {
        if (!path) continue;
        if (loaded[key]) continue; // already have base64

        if (/^(https?:|data:|blob:)/i.test(path)) {
          loaded[key] = path;
          if (key === "client") setPatientSigLink(path);
          if (key === "guardian") setGuardianSigLink(path);
          continue;
        }

        try {
          const res = await fetch(
            `${API_BASE_URL}/PatientDocument/ViewPatientDocument?path=${encodeURIComponent(
              path
            )}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
                Accept: "text/plain, */*",
              },
            }
          );
          if (!res.ok) continue;

          const ct = (res.headers.get("content-type") || "").toLowerCase();

          if (ct.includes("text")) {
            const link = (await res.text()).trim();
            if (link) {
              loaded[key] = link;
              if (key === "client") setPatientSigLink(link);
              if (key === "guardian") setGuardianSigLink(link);
            }
          } else {
            const blob = await res.blob();
            if (blob.size > 0) {
              const objUrl = URL.createObjectURL(blob);
              loaded[key] = objUrl;
              if (key === "client") setPatientSigLink(objUrl);
              if (key === "guardian") setGuardianSigLink(objUrl);
            }
          }
        } catch (err) {
          console.warn("Failed to load signature:", key, err);
        }
      }

      if (active) setSignatures(loaded);
    };

    const fetchAll = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem(
          "neuro_patientportal_auth_token"
        );
        if (!token) throw new Error("Authentication token missing.");

        const [formRes, itpRes] = await Promise.all([
          fetch(
            `${API_BASE_URL}/Forms/GetPatientFormsbyId?Id=${patientFormId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            }
          ),
          fetch(
            `${API_BASE_URL}/Rehabilitative/getIndividualtreatment?patientformID=${patientFormId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          ),
        ]);

        if (itpRes.status === 204) {
          setItp({});
          setForm({});
          setFormMeta(null);
          setLoading(false);
          return;
        }
        if (!formRes.ok || !itpRes.ok)
          throw new Error("Failed to load ARMHS ITP form.");

        const [formDataRaw, itpDataRaw] = await Promise.all([
          formRes.json(),
          itpRes.json(),
        ]);

        const formData = Array.isArray(formDataRaw)
          ? formDataRaw[0] || {}
          : formDataRaw || {};
        const itpData = Array.isArray(itpDataRaw)
          ? itpDataRaw[0] || {}
          : itpDataRaw || {};

        setForm(formData);
        setFormMeta(formData || null);
        setItp(itpData);
        await fetchSignatures(itpData, formData, token);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
    return () => {
      active = false;
    };
  }, [API_BASE_URL, patientFormId]);

  if (loading)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          height: "60vh",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  if (error)
    return (
      <Typography color="error" align="center" sx={{ p: 3 }}>
        {error}
      </Typography>
    );
  if (!itp)
    return (
      <Typography color="textSecondary" align="center" sx={{ p: 3 }}>
        No data available.
      </Typography>
    );

  const fileNo =
    pickNonEmpty(
      itp.fileno,
      form.fileno,
      form.accountNo,
      form.fileNo,
      "—"
    ) || "—";
  const patientName =
    pickNonEmpty(itp.patientName, form.patientName, "—") || "—";
  const dobDisplay = toDate(
    pickNonEmpty(itp.dateOfBirth, form.dob, form.dateOfBirth)
  );

  const dosDisplay = toDate(
    pickNonEmpty(itp.assessmentDate, form.assessmentDate)
  );
  const dateFA = toDate(pickNonEmpty(itp.dateFA, itp.recentFA));
  const dateDA = toDate(pickNonEmpty(itp.dateDA, itp.recentDA));
  const planDateDisplay = fmtMMDDYYYY(
    pickNonEmpty(itp.planDate, itp.Plandate)
  );

  const titleDisplay = pickNonEmpty(
    form.Formtitle,
    form.customFormTitle,
    "—"
  );

  const functionalBarriers = pickNonEmpty(itp.functionalBarriers, "");
  const strengths = pickNonEmpty(itp.strengths, "");
  const culturalNeeds = pickNonEmpty(itp.culturalNeeds, "");
  const clientRecovery = pickNonEmpty(
    itp.clientRecovery,
    itp.recoveryVision,
    ""
  );
  const summary = pickNonEmpty(itp.summary, itp.progressSummary, "");
  const clientHistory = pickNonEmpty(itp.clientHistory, "");

  const rowsindividuals = ensureArray(
    pickNonEmpty(
      itp.individualslist,
      itp.rowsindividuals,
      itp.individualsInvolvedList,
      itp.individualsinvolvedlist
    )
  );
  const rowsRehabGoal = ensureArray(
    pickNonEmpty(
      itp.rehabGoallist,
      itp.rowsRehabGoal,
      itp.rehabGoal,
      itp.rehabGoals
    )
  );
  const rowscoordinationServices = ensureArray(
    pickNonEmpty(
      itp.coordinationServiceslist,
      itp.rowscoordinationServices,
      itp.coordinationServicesList
    )
  );

  const sitReading = pickNonEmpty(
    itp.agreetreatmentplan,
    itp.sitReading,
    ""
  );

  const printed = !!pickNonEmpty(itp.isPrinted, form.isPrinted, false);
  const inactive = !!pickNonEmpty(itp.inactive, form.inactive, false);
  const auditReady = !!pickNonEmpty(
    itp.isAuditReady,
    form.isAuditReady,
    false
  );
  const locked = !!pickNonEmpty(
    itp.isChecked,
    form.isChecked,
    form.formlock,
    false
  );

  const normalizeMsg = (s) => {
    const t = String(s || "").trim();
    if (!t) return "";
    return t.replace(/\s*\.*\s*$/, ".");
  };
  const practitionerMsg = normalizeMsg(
    pickNonEmpty(itp.messagepractitioner, form.messagepractitioner)
  );
  const directorMsg = normalizeMsg(
    pickNonEmpty(
      itp.messagetreatmentdirector,
      form.messagetreatmentdirector
    )
  );
  const professionalMsg = normalizeMsg(
    pickNonEmpty(
      itp.massagename,
      itp.messageProfessionalSignature,
      itp.professional,
      form.message
    )
  );

  const clientSignedText = fmtMMDDYYYY(
    pickNonEmpty(
      itp.clientDate,
      form.patientSignatureDATE,
      form.clientSignatureDATE
    )
  );
  const guardianSignedText = fmtMMDDYYYY(
    pickNonEmpty(itp.legalGuardianDate, form.legalGuardianDate)
  );

  return (
    <>
      <Paper
        sx={{
          p: 4,
          bgcolor: "#fff",
          borderRadius: 2,
          boxShadow: "0 3px 8px rgba(0,0,0,0.08)",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: "-16px",
            zIndex: (theme) => theme.zIndex.appBar,
            bgcolor: "#fff",
            p: 4,
            pb: 0,
            mb: 3,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{ position: "absolute", top: 16, left: 16 }}
          >
            <strong>DOS:</strong> {dosDisplay || "—"}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              position: "absolute",
              top: 16,
              left: "50%",
              transform: "translateX(-50%)",
              fontWeight: "bold",
            }}
          >
            ARMHS ITP
          </Typography>
          <Box sx={{ height: 28 }} />
          <Divider sx={{ mx: -4 }} />
        </Box>

        <Grid
          container
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Grid item xs={12} md={4}>
            <Typography variant="body2">
              <strong>File #:</strong> {fileNo || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4} textAlign="center">
            <Typography variant="body2">
              <strong>Client Name:</strong> {patientName || "—"}
            </Typography>
          </Grid>
          <Grid
            item
            xs={12}
            md={4}
            textAlign={{ xs: "left", md: "right" }}
          >
            <Typography variant="body2">
              <strong>DOB:</strong> {dobDisplay || "—"}
            </Typography>
          </Grid>
        </Grid>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 4,
              mb: 1,
            }}
          >
            <TextField
              label="Title"
              value={titleDisplay || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
            <TextField
              label="Assessment Date"
              value={dosDisplay || "—"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              sx={headerFieldSx}
            />
          </Box>
        </Box>

        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
          Document Status
        </Typography>
        <Grid container spacing={1} sx={{ mb: 3 }}>
          <Grid item>
            <FormControlLabel
              control={<Checkbox disabled checked={printed} />}
              label="Printed and Filed"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox disabled checked={!!inactive} />}
              label="Inactive"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox disabled checked={auditReady} />}
              label="Audit Ready"
            />
          </Grid>
          <Grid item>
            <FormControlLabel
              control={<Checkbox disabled checked={locked} />}
              label="Lock"
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Individuals involved table */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    width: "50%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                    color: "black",
                    textAlign: "left",
                  }}
                >
                  Individuals involved in the development of this ITP
                </th>
                <th
                  style={{
                    width: "50%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                    color: "black",
                    textAlign: "left",
                  }}
                >
                  Client/Agency/Title/Family Members/Others (specify)
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsindividuals.length > 0 ? (
                rowsindividuals.map((individual, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        width: "50%",
                        padding: "8px",
                        border: "1px solid black",
                        verticalAlign: "top",
                        color: "black",
                      }}
                    >
                      {pickNonEmpty(
                        individual?.developmentofthisITP,
                        individual?.name,
                        "N/A"
                      )}
                    </td>
                    <td
                      style={{
                        width: "50%",
                        padding: "8px",
                        border: "1px solid black",
                        verticalAlign: "top",
                      }}
                    >
                      {pickNonEmpty(
                        individual?.familyMembers,
                        individual?.details,
                        "N/A"
                      )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={2}
                    style={{
                      padding: "8px",
                      border: "1px solid black",
                      textAlign: "center",
                    }}
                  >
                    <Typography>No individuals listed</Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Grid>

        {/* FA & DA dates */}
        <Box sx={{ mb: 2, mt: 2 }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
              },
              gap: 4,
              mb: 1,
            }}
          >
            <TextField
              label="Date of most recent Diagnostic Assessment"
              value={dateDA || "N/A"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Date of most recent Functional Assessment"
              value={dateFA || "N/A"}
              size="small"
              fullWidth
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </Box>

        {/* Text areas */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            label="Functional Barriers to be Addressed in this Plan"
            value={functionalBarriers || "N/A"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            label="Strengths and Resources"
            value={strengths || "N/A"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            label="Cultural Considerations and Needs"
            value={culturalNeeds || "N/A"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            label="Client Recovery Vision"
            value={clientRecovery || "N/A"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <TextField
            label="Summary of Progress on Previous Goals and Objectives"
            value={summary || "N/A"}
            fullWidth
            multiline
            minRows={3}
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        {/* Rehab goals, objectives, interventions */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          {/* Rehab Goals table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "60%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Rehab Goal
                </th>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "20%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  *MN
                </th>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "20%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Modality
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsRehabGoal.length > 0 ? (
                rowsRehabGoal.map((item, index) => (
                  <tr key={index}>
                    <td
                      style={{
                        width: "60%",
                        padding: "8px",
                        border: "1px solid black",
                        verticalAlign: "top",
                      }}
                    >
                      {pickNonEmpty(
                        item?.rehabStatus,
                        item?.goal,
                        "N/A"
                      )}
                    </td>
                    <td
                      style={{
                        width: "20%",
                        padding: "8px",
                        border: "1px solid black",
                        verticalAlign: "top",
                      }}
                    >
                      {pickNonEmpty(item?.mN, item?.mn, "N/A")}
                    </td>
                    <td
                      style={{
                        width: "20%",
                        padding: "8px",
                        border: "1px solid black",
                        verticalAlign: "top",
                      }}
                    >
                      {pickNonEmpty(item?.modality, "N/A")}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: "8px",
                      border: "1px solid black",
                      textAlign: "center",
                    }}
                  >
                    <Typography>No rehab goals available</Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Objectives table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
              tableLayout: "fixed",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "60%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Objective(s)
                </th>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "20%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Timeframe
                </th>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "20%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Frequency
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsRehabGoal.length > 0 ? (
                rowsRehabGoal.flatMap((item, index) =>
                  ensureArray(item?.rehabobjective).map(
                    (obj, objIndex) => (
                      <tr key={`${index}-${objIndex}`}>
                        <td
                          style={{
                            width: "60%",
                            padding: "8px",
                            border: "1px solid black",
                            verticalAlign: "top",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {pickNonEmpty(
                            obj?.objectives,
                            obj?.objective,
                            "N/A"
                          )}
                        </td>
                        <td
                          style={{
                            width: "20%",
                            padding: "8px",
                            border: "1px solid black",
                            verticalAlign: "top",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {pickNonEmpty(obj?.timeframe, "N/A")}
                        </td>
                        <td
                          style={{
                            width: "20%",
                            padding: "8px",
                            border: "1px solid black",
                            verticalAlign: "top",
                            wordWrap: "break-word",
                            whiteSpace: "normal",
                          }}
                        >
                          {pickNonEmpty(obj?.frequency, "N/A")}
                        </td>
                      </tr>
                    )
                  )
                )
              ) : (
                <tr>
                  <td
                    colSpan={3}
                    style={{
                      padding: "8px",
                      border: "1px solid black",
                      textAlign: "center",
                    }}
                  >
                    <Typography>No objectives available</Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Interventions table */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              border: "1px solid black",
              marginTop: "10px",
            }}
          >
            <thead>
              <tr>
                <th
                  style={{
                    color: "black",
                    textAlign: "center",
                    width: "100%",
                    padding: "8px",
                    border: "1px solid black",
                    fontWeight: 700,
                  }}
                >
                  Interventions/Treatment Strategies
                </th>
              </tr>
            </thead>
            <tbody>
              {rowsRehabGoal.length > 0 ? (
                rowsRehabGoal.flatMap((item, index) =>
                  ensureArray(item?.interventionsStrategies).map(
                    (intervention, intIndex) => (
                      <tr key={`${index}-${intIndex}`}>
                        <td
                          style={{
                            width: "100%",
                            padding: "8px",
                            border: "1px solid black",
                            verticalAlign: "top",
                          }}
                        >
                          {pickNonEmpty(
                            intervention?.interventions,
                            "N/A"
                          )}
                        </td>
                      </tr>
                    )
                  )
                )
              ) : (
                <tr>
                  <td
                    style={{
                      padding: "8px",
                      border: "1px solid black",
                      textAlign: "center",
                    }}
                  >
                    <Typography>No interventions available</Typography>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Grid>

        {/* Additional text sections */}
        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            sx={{
              textAlign: "left",
              textDecoration: "underline",
              fontWeight: 700,
            }}
          >
            If a client has a history of not engaging in treatment, describe the
            treatment strategy to engage the client:
          </Typography>
          <Typography>{clientHistory || "N/A"}</Typography>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            sx={{ textDecoration: "underline", fontWeight: 700 }}
          >
            This individual received a copy of this plan
          </Typography>
          <table
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <tbody>
              <tr>
                <td
                  style={{
                    padding: "8px",
                    width: "10%",
                    verticalAlign: "middle",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      String(
                        pickNonEmpty(
                          itp.individualCopy,
                          itp.individualCopyValue
                        )
                      )
                        .toLowerCase()
                        .trim() === "yes"
                    }
                    disabled
                    style={{
                      marginLeft: "10px",
                      transform: "scale(1.0)",
                      marginRight: "5px",
                    }}
                  />
                  <Typography
                    component="label"
                    style={{ fontSize: "14px" }}
                  >
                    Yes
                  </Typography>
                </td>
                <td
                  style={{
                    padding: "8px",
                    width: "10%",
                    verticalAlign: "middle",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={
                      String(
                        pickNonEmpty(
                          itp.individualCopy,
                          itp.individualCopyValue
                        )
                      )
                        .toLowerCase()
                        .trim() === "no"
                    }
                    disabled
                    style={{
                      marginLeft: "10px",
                      transform: "scale(1.0)",
                      marginRight: "5px",
                    }}
                  />
                  <Typography
                    component="label"
                    style={{ fontSize: "14px" }}
                  >
                    No
                  </Typography>
                </td>
                <td
                  style={{
                    padding: "8px",
                    width: "80%",
                    verticalAlign: "middle",
                  }}
                >
                  <Typography>
                    If no, specify reason:{" "}
                    {pickNonEmpty(itp.reason, itp.noCopyReason, "N/A")}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            style={{
              fontWeight: "bold",
              color: "black",
              fontSize: "16px",
            }}
          >
            Signature
          </Typography>

          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 8,
            }}
          >
            <tbody>
              {/* Client Signature (editable) */}
              <tr>
                <td
                  style={{
                    width: "25%",
                    verticalAlign: "middle",
                    padding: "8px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                  >
                    Client
                  </Typography>
                </td>
                <td
                  style={{
                    width: "75%",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {patientSigLink ? (
                      <>
                        <div
                          style={{
                            width: 200,
                            height: 80,
                            border: "2px solid #000",
                            background: "#f5f5f5",
                            borderRadius: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={patientSigLink}
                            alt="Client Signature"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        {clientSignedText !== "N/A" && (
                          <Typography sx={{ fontWeight: 700 }}>
                            Signed on {clientSignedText}
                          </Typography>
                        )}
                        <Tooltip title="Edit Signature" arrow>
                          <Button
                            onClick={openClientModal}
                            variant="outlined"
                            size="small"
                            style={{
                              minWidth: "32px",
                              padding: "4px",
                            }}
                            aria-label="Edit Signature"
                          >
                            <EditIcon />
                          </Button>
                        </Tooltip>

                        <Tooltip title="Remove Signature" arrow>
                          <Button
                            onClick={handleDeleteClientSignature}
                            style={{
                              minWidth: "32px",
                              padding: "4px",
                            }}
                            aria-label="Remove Signature"
                          >
                            <RemoveCircleOutlineIcon
                              style={{ color: "red" }}
                            />
                          </Button>
                        </Tooltip>
                      </>
                    ) : (
                      <Link component="button" onClick={openClientModal}>
                        Open signing window
                      </Link>
                    )}
                  </Box>
                </td>
              </tr>

              {/* Guardian Signature (read-only) */}
              <tr>
                <td
                  style={{
                    width: "25%",
                    verticalAlign: "middle",
                    padding: "8px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                  >
                    Client&apos;s Legal Guardian
                  </Typography>
                </td>
                <td
                  style={{
                    width: "75%",
                    padding: "8px",
                    verticalAlign: "middle",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flexWrap: "wrap",
                    }}
                  >
                    {guardianSigLink ? (
                      <>
                        <div
                          style={{
                            width: 200,
                            height: 80,
                            border: "2px solid #000",
                            background: "#f5f5f5",
                            borderRadius: 8,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={guardianSigLink}
                            alt="Guardian Signature"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                            }}
                          />
                        </div>
                        {guardianSignedText !== "N/A" && (
                          <Typography sx={{ fontWeight: 700 }}>
                            Signed on {guardianSignedText}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography>—</Typography>
                    )}
                  </Box>
                </td>
              </tr>

              {/* Practitioner, Director, MHP text signatures */}
              <tr>
                <td
                  style={{
                    width: "75%",
                    verticalAlign: "top",
                    padding: "8px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                  >
                    Mental Health Practitioner
                  </Typography>
                </td>
                <td
                  colSpan={2}
                  style={{
                    padding: "8px",
                    verticalAlign: "top",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: "bold", color: "black" }}
                  >
                    {practitionerMsg || "__"}
                  </Typography>
                </td>
              </tr>

              <tr>
                <td
                  style={{
                    width: "25%",
                    verticalAlign: "top",
                    padding: "8px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                  >
                    ARMHS Treatment Director
                  </Typography>
                </td>
                <td
                  colSpan={2}
                  style={{
                    padding: "8px",
                    verticalAlign: "top",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: "bold", color: "black" }}
                  >
                    {directorMsg || "__"}
                  </Typography>
                </td>
              </tr>

              <tr>
                <td
                  style={{
                    width: "25%",
                    verticalAlign: "top",
                    padding: "8px",
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 600 }}
                  >
                    Mental Health Professional
                  </Typography>
                </td>
                <td
                  colSpan={2}
                  style={{
                    padding: "8px",
                    verticalAlign: "top",
                  }}
                >
                  <Typography
                    sx={{ fontWeight: "bold", color: "black" }}
                  >
                    {professionalMsg || "__"}
                  </Typography>
                </td>
              </tr>
            </tbody>
          </table>
        </Grid>

        <Grid item xs={12} sx={{ mt: 2 }}>
          <Typography
            sx={{ textDecoration: "underline", fontWeight: 700 }}
          >
            Plan Update:
          </Typography>
          <Typography sx={{ mt: 1 }}>
            This plan must be updated at least every 180 days or more often when
            there is significant change in the client&apos;s situation or
            functioning, or in services or service methods to be used or at the
            request of the client or the client&apos;s legal guardian.
          </Typography>
          <Typography sx={{ mt: 1 }}>
            Proposed date of ITP meeting to update plan: {planDateDisplay}
          </Typography>
        </Grid>

        {/* Save Form Button */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSaveForm}
          >
            Save Form
          </Button>
        </Box>
      </Paper>

      {/* Client Signature Modal */}
      <Dialog
        open={openClientSignatureModal}
        onClose={closeClientModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Client Signature</DialogTitle>
        <DialogContent>
          <Box sx={{ border: "1px solid #000", mt: 1 }}>
            <SignatureField
              ref={clientSigCanvasRef}
             
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="contained"
            onClick={handleSaveClientSignature}
            sx={{ minWidth: 120, mr: 2 }}
          >
            Save
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (clientSigCanvasRef.current) {
                clientSigCanvasRef.current.clear();
              }
            }}
            sx={{ minWidth: 120 }}
          >
            Clear
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ARMHSITPPreview;